// The shell's own word syntax, in both directions: what curl was actually handed once the shell had finished with
// the line, and how a value has to be spelled to survive being handed back to one.

// The two a command is read in and written back for: bash, and the cmd Chrome's "Copy as cURL (cmd)" is written for.
export type Shell = "bash" | "cmd";

export interface Word {
  text: string;
  // Whether any of it stood inside quotes, which is the only thing that tells `|` the pipe from `|` the value.
  quoted: boolean;
  // The references the shell would have expanded, as written, which is what keeps them expanding through a rewrite.
  variables: string[];
}

export interface Split {
  words: Word[];
  error: string | null;
  shell: Shell;
}

export const UNTERMINATED_QUOTE = "A quote in the command is never closed";
export const POWERSHELL =
  "A line ending in a backtick is PowerShell's, which the page does not read: write the command for bash or cmd";

export function splitWords(source: string): Split {
  const words: Word[] = [];
  let text = "";
  // A word exists once anything has opened it, which `''` does while adding no characters to it.
  let started = false;
  let quoted = false;
  let variables: string[] = [];
  let at = 0;

  const finish = () => {
    if (!started) return;
    words.push({ text, quoted, variables });
    text = "";
    started = false;
    quoted = false;
    variables = [];
  };

  const unterminated = (): Split => ({ words, error: UNTERMINATED_QUOTE, shell: "bash" });

  while (at < source.length) {
    const char = source[at];

    if (SPACE.test(char)) {
      finish();
      at += 1;
      continue;
    }

    // A `#` opens a comment only where a word does: the one in a fragment is a character of the address.
    if (char === "#" && !started) {
      while (at < source.length && source[at] !== "\n") at += 1;
      continue;
    }

    if (char === "\\") {
      const next = source[at + 1];
      // A backslash with nothing after it is the line the command was going to be continued on.
      if (next === undefined) break;
      // The one escape that joins rather than escapes, and the reason a curl command reads down the page.
      if (next === "\n") {
        at += 2;
        continue;
      }
      if (next === "\r") {
        at += source[at + 2] === "\n" ? 3 : 2;
        continue;
      }
      started = true;
      text += next;
      at += 2;
      continue;
    }

    if (char === "'") {
      const end = source.indexOf("'", at + 1);
      if (end < 0) return unterminated();
      started = true;
      quoted = true;
      text += source.slice(at + 1, end);
      at = end + 1;
      continue;
    }

    if (char === "\"") {
      const read = readDouble(source, at + 1);
      if (!read) return unterminated();
      started = true;
      quoted = true;
      text += read.text;
      variables.push(...read.variables);
      at = read.at;
      continue;
    }

    // Chrome copies a body with a control byte in it as $'…', which is the one quoting where \n means a newline.
    if (char === "$" && source[at + 1] === "'") {
      const read = readAnsi(source, at + 2);
      if (!read) return unterminated();
      started = true;
      quoted = true;
      text += read.text;
      at = read.at;
      continue;
    }

    // A locale-translated string is the string, there being no catalogue on anybody's machine to look it up in.
    if (char === "$" && source[at + 1] === "\"") {
      const read = readDouble(source, at + 2);
      if (!read) return unterminated();
      started = true;
      quoted = true;
      text += read.text;
      variables.push(...read.variables);
      at = read.at;
      continue;
    }

    const reference = char === "$" ? referenceAt(source, at) : null;
    if (reference) {
      started = true;
      text += reference;
      variables.push(reference);
      at += reference.length;
      continue;
    }

    // An unquoted caret before a quote or a line break is cmd's escape and no bash command's, so the line is cmd's.
    if (char === "^" && isOneOf(source[at + 1], "\"\r\n")) return splitCmd(source);

    if (char === "`" && isOneOf(source[at + 1], "\r\n")) return { words, error: POWERSHELL, shell: "bash" };

    started = true;
    text += char;
    at += 1;
  }

  finish();
  return { words, error: null, shell: "bash" };
}

// Single quotes hold every byte but their own, so a quote inside one is spelled by leaving the quoting for it.
export function quoteWord(value: string, variables: readonly string[] = []): string {
  if (value === "") return "''";
  if (PLAIN.test(value)) return value;
  if (holdsVariable(value, variables)) return quoteDouble(value, variables);
  return `'${value.replace(/'/g, "'\\''")}'`;
}

// Chrome's spelling, every value in quotes cmd is told to leave alone, which keeps a command of plain values cmd's.
export function quoteCmd(value: string, variables: readonly string[] = []): string {
  let out = "";

  for (let at = 0; at < value.length;) {
    const reference = liveAt(value, at, variables);
    if (reference) {
      out += reference;
      at += reference.length;
      continue;
    }

    const char = value[at];

    // The C runtime reads a backslash as one only before a quote, and the one closing the value is a quote too.
    if (char === "\\") {
      let end = at;
      while (value[end] === "\\") end += 1;
      out += "\\".repeat(end === value.length || value[end] === "\"" ? (end - at) * 2 : end - at);
      at = end;
      continue;
    }

    // A caret ending a line escapes the line break after it, and the empty line after that is the one kept.
    if (char === "\r" || char === "\n") {
      out += "^\n\n";
      at += char === "\r" && value[at + 1] === "\n" ? 2 : 1;
      continue;
    }

    // cmd expands %NAME% before it reads a caret, so the name is broken up rather than the percent escaped.
    if (char === "%") out += /\w/.test(value[at + 1] ?? "") ? "%^" : "%";
    else if (char === "\"") out += "^\\^\"";
    else out += CMD_SPECIAL.includes(char) ? `^${char}` : char;
    at += 1;
  }

  return `^"${out}^"`;
}

// Everything a shell leaves alone. `~`, `{`, `!`, `*` and `?` are left out on purpose: each is a character the shell
// would read as an instruction, and a URL that carries one has to arrive at curl the way it was typed.
const PLAIN = /^[A-Za-z0-9_@%+=:,./-]+$/;

const SPACE = /\s/;

// Inside double quotes a backslash escapes only these four; before anything else it is a backslash somebody typed.
const DOUBLE_ESCAPES = "\"\\$`";

// A name or a braced expression, which is as far as the page reads a reference: `$(…)` is a command it cannot run.
const REFERENCE = /\$(?:[A-Za-z_]\w*|\{[^}]+\})/y;

// cmd's own references, which it expands before anything else on the line and inside quotes as well.
const PERCENT = /%[A-Za-z_]\w*%/g;

// What cmd acts on outside quotes; everything else it passes to curl.exe as it stands.
const CMD_SPECIAL = "^&|<>!";

const CMD_OPERATORS = "&|<>";

function isOneOf(char: string | undefined, chars: string): boolean {
  return char !== undefined && chars.includes(char);
}

function referenceAt(source: string, at: number): string | null {
  REFERENCE.lastIndex = at;
  return REFERENCE.exec(source)?.[0] ?? null;
}

// A reference the shell expanded when it was read, and still standing where one would be read as the same name.
function liveAt(value: string, at: number, variables: readonly string[]): string | null {
  for (const reference of variables) {
    if (!value.startsWith(reference, at)) continue;
    // `$TOKEN` followed by a letter would be read as a longer name, so it only still expands where its name ends.
    if (/\w$/.test(reference) && /\w/.test(value[at + reference.length] ?? "")) continue;
    return reference;
  }
  return null;
}

function holdsVariable(value: string, variables: readonly string[]): boolean {
  for (let at = 0; at < value.length; at += 1) {
    if (liveAt(value, at, variables)) return true;
  }
  return false;
}

// Double quotes are the one quoting a variable still expands in, so every other `$` in the value is escaped.
function quoteDouble(value: string, variables: readonly string[]): string {
  let out = "";

  for (let at = 0; at < value.length;) {
    const reference = liveAt(value, at, variables);
    if (reference) {
      out += reference;
      at += reference.length;
      continue;
    }

    const char = value[at];
    // History expansion reads `!` inside double quotes too, so it is spelled by leaving the quotes for it.
    if (char === "!") out += "\"\\!\"";
    else out += DOUBLE_ESCAPES.includes(char) ? `\\${char}` : char;
    at += 1;
  }

  return `"${out}"`;
}

function readDouble(source: string, from: number): Read | null {
  let text = "";
  const variables: string[] = [];
  let at = from;

  while (at < source.length) {
    const char = source[at];
    if (char === "\"") return { text, at: at + 1, variables };

    if (char === "\\") {
      const next = source[at + 1];
      if (next === undefined) break;
      if (next === "\n") {
        at += 2;
        continue;
      }
      if (DOUBLE_ESCAPES.includes(next)) {
        text += next;
        at += 2;
        continue;
      }
    }

    const reference = char === "$" ? referenceAt(source, at) : null;
    if (reference) {
      text += reference;
      variables.push(reference);
      at += reference.length;
      continue;
    }

    text += char;
    at += 1;
  }

  return null;
}

function readAnsi(source: string, from: number): Read | null {
  let text = "";
  let at = from;

  while (at < source.length) {
    const char = source[at];
    if (char === "'") return { text, at: at + 1, variables: [] };

    if (char !== "\\") {
      text += char;
      at += 1;
      continue;
    }

    const next = source[at + 1];
    if (next === undefined) break;

    const simple = ANSI_ESCAPES[next];
    if (simple !== undefined) {
      text += simple;
      at += 2;
      continue;
    }

    // The numeric forms, each taking as many digits as it is allowed and no more.
    const numeric = NUMERIC.exec(source.slice(at + 1));
    if (numeric) {
      const [whole] = numeric;
      const radix = whole[0] === "x" || whole[0] === "u" || whole[0] === "U" ? 16 : 8;
      const digits = radix === 16 ? whole.slice(1) : whole;
      text += String.fromCodePoint(parseInt(digits, radix));
      at += 1 + whole.length;
      continue;
    }

    text += next;
    at += 2;
  }

  return null;
}

interface Read {
  text: string;
  at: number;
  variables: string[];
}

const ANSI_ESCAPES: Record<string, string> = {
  a: "\x07",
  b: "\b",
  e: "\x1b",
  E: "\x1b",
  f: "\f",
  n: "\n",
  r: "\r",
  t: "\t",
  v: "\v",
  "\\": "\\",
  "'": "'",
  "\"": "\"",
  "?": "?",
};

const NUMERIC = /^(?:x[0-9a-fA-F]{1,2}|u[0-9a-fA-F]{1,4}|U[0-9a-fA-F]{1,8}|[0-7]{1,3})/;

// Two passes, as on Windows: cmd takes its carets off and hands the line, quotes and all, to curl.exe's C runtime.
function splitCmd(source: string): Split {
  // cmd drops every carriage return before it reads a line, which is why it has no way of writing one.
  const line = source.replace(/\r/g, "");
  const references = new Map<number, string>();
  for (const match of line.matchAll(PERCENT)) references.set(match.index, match[0]);

  let handed = "";
  // Where in what cmd hands on a reference it expanded starts, and which characters it was told to leave alone.
  const live = new Map<number, string>();
  const escaped = new Set<number>();
  let quoting = false;
  let operator: string | null = null;
  let at = 0;

  const take = (index: number, escape: boolean): number => {
    const reference = references.get(index);
    if (reference) {
      live.set(handed.length, reference);
      handed += reference;
      return reference.length;
    }
    if (escape) escaped.add(handed.length);
    handed += line[index];
    return 1;
  };

  while (at < line.length) {
    const char = line[at];

    // A line cmd was not told to continue ends the command, as `&` would on the one line.
    if (char === "\n") {
      if (line.slice(at).trim() !== "") operator = "&";
      break;
    }

    if (references.has(at)) {
      at += take(at, false);
      continue;
    }

    // Inside cmd's own quotes a caret is a caret and an operator a character, until the next quote closes them.
    if (quoting) {
      if (char === "\"") quoting = false;
      handed += char;
      at += 1;
      continue;
    }

    if (char === "^") {
      let next = at + 1;
      // A caret ending a line joins the next one on and escapes its first character, a second line break among them.
      if (line[next] === "\n") next += 1;
      if (next >= line.length) break;
      at = next + take(next, true);
      continue;
    }

    if (CMD_OPERATORS.includes(char)) {
      operator = char;
      break;
    }

    if (char === "\"") quoting = true;
    handed += char;
    at += 1;
  }

  const words = splitArguments(handed, live, escaped);
  if (operator) words.push({ text: operator, quoted: false, variables: [] });
  return { words, error: null, shell: "cmd" };
}

// The C runtime's rules, which read a backslash as an escape only in a run of them that ends at a quote.
function splitArguments(handed: string, live: Map<number, string>, escaped: Set<number>): Word[] {
  const words: Word[] = [];
  let text = "";
  let started = false;
  let quoted = false;
  let variables: string[] = [];
  let inside = false;
  let at = 0;

  const finish = () => {
    if (!started) return;
    words.push({ text, quoted, variables });
    text = "";
    started = false;
    quoted = false;
    variables = [];
  };

  while (at < handed.length) {
    const reference = live.get(at);
    if (reference) {
      started = true;
      text += reference;
      variables.push(reference);
      at += reference.length;
      continue;
    }

    const char = handed[at];

    if (!inside && (char === " " || char === "\t")) {
      finish();
      at += 1;
      continue;
    }

    started = true;
    if (escaped.has(at)) quoted = true;

    if (char === "\\") {
      let end = at;
      while (handed[end] === "\\") end += 1;
      const count = end - at;
      if (handed[end] !== "\"") {
        text += "\\".repeat(count);
        at = end;
        continue;
      }
      text += "\\".repeat(count >> 1);
      if (count % 2 === 1) {
        text += "\"";
        at = end + 1;
        continue;
      }
      at = end;
      continue;
    }

    if (char === "\"") {
      quoted = true;
      if (inside && handed[at + 1] === "\"") {
        text += "\"";
        at += 2;
        continue;
      }
      inside = !inside;
      at += 1;
      continue;
    }

    text += char;
    at += 1;
  }

  finish();
  return words;
}
